import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiPlus, FiTrendingUp, FiBarChart2, FiClock, FiChevronRight } from 'react-icons/fi';
import { getApiUrl } from '../utils/api';
import CFTCDisclaimer from '../components/CFTCDisclaimer';

interface Strategy {
  id: number;
  name: string;
  description: string | null;
  project_id: number | null;
  is_public: boolean;
  created_at: string;
  updated_at: string;
  version_count: number;
  backtest_count: number;
  latest_metrics: {
    sharpe?: number;
    cagr?: number;
    max_drawdown?: number;
  } | null;
}

export default function StrategiesPage() {
  const [strategies, setStrategies] = useState<Strategy[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newStrategyName, setNewStrategyName] = useState('');
  const [newStrategyDescription, setNewStrategyDescription] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    if (!token) {
      navigate('/login');
      return;
    }
    fetchStrategies();
  }, [navigate]);

  const fetchStrategies = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/api/v1/strategies`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setStrategies(data);
      }
    } catch (error) {
      console.error('Error fetching strategies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateStrategy = async () => {
    if (!newStrategyName.trim()) return;

    setCreating(true);
    setCreateError(null);
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(`${getApiUrl()}/api/v1/strategies`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: newStrategyName.trim(),
          description: newStrategyDescription.trim() || null
        })
      });

      if (response.ok) {
        const newStrategy = await response.json();
        setStrategies([newStrategy, ...strategies]);
        setShowCreateModal(false);
        setNewStrategyName('');
        setNewStrategyDescription('');
        setCreateError(null);
      } else {
        const error = await response.json();
        setCreateError(error.detail || 'Failed to create strategy');
      }
    } catch (error) {
      console.error('Error creating strategy:', error);
      setCreateError('Failed to create strategy. Please try again.');
    } finally {
      setCreating(false);
    }
  };

  const formatMetric = (value: number | undefined, prefix: string = '', suffix: string = ''): string => {
    if (value === undefined || value === null) return '-';
    return `${prefix}${value.toFixed(2)}${suffix}`;
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-2xl font-semibold text-white">Strategies</h1>
            <p className="text-gray-400 mt-1">
              {strategies.length} strateg{strategies.length !== 1 ? 'ies' : 'y'}
            </p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white text-black font-medium rounded-md hover:bg-gray-100 transition-colors"
          >
            <FiPlus className="w-4 h-4" />
            New Strategy
          </button>
        </div>

        {/* Strategies List */}
        {strategies.length === 0 ? (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiTrendingUp className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-white font-medium mb-2">No strategies yet</h3>
            <p className="text-gray-400 text-sm mb-6">
              Create your first strategy to start tracking backtests
            </p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-white text-black font-medium rounded-md hover:bg-gray-100 transition-colors"
            >
              Create Strategy
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            {strategies.map((strategy) => (
              <button
                key={strategy.id}
                onClick={() => navigate(`/dashboard/strategies/${strategy.id}`)}
                className="w-full text-left bg-gray-900 border border-gray-800 rounded-md p-5 hover:border-gray-700 transition-colors group"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="font-medium text-white truncate">{strategy.name}</h3>
                      {strategy.is_public && (
                        <span className="text-xs px-2 py-0.5 bg-blue-900/30 text-blue-400 rounded-full">
                          Public
                        </span>
                      )}
                    </div>
                    {strategy.description && (
                      <p className="text-sm text-gray-500 mb-3 line-clamp-1">
                        {strategy.description}
                      </p>
                    )}
                    <div className="flex items-center gap-6 text-sm">
                      <span className="text-gray-500 flex items-center gap-1.5">
                        <FiBarChart2 className="w-3.5 h-3.5" />
                        {strategy.backtest_count} backtest{strategy.backtest_count !== 1 ? 's' : ''}
                      </span>
                      <span className="text-gray-500 flex items-center gap-1.5">
                        <FiClock className="w-3.5 h-3.5" />
                        Updated {formatDate(strategy.updated_at)}
                      </span>
                    </div>
                  </div>

                  {/* Metrics Summary */}
                  <div className="flex items-center gap-6 ml-6">
                    {strategy.latest_metrics && (
                      <div className="flex items-center gap-4 text-right">
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Sharpe</p>
                          <p className={`text-sm font-medium tabular-nums ${
                            (strategy.latest_metrics.sharpe ?? 0) >= 1
                              ? 'text-profit'
                              : 'text-gray-400'
                          }`}>
                            {formatMetric(strategy.latest_metrics.sharpe)}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">CAGR</p>
                          <p className={`text-sm font-medium tabular-nums ${
                            (strategy.latest_metrics.cagr ?? 0) > 0
                              ? 'text-profit'
                              : 'text-loss'
                          }`}>
                            {formatMetric(strategy.latest_metrics.cagr, '', '%')}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500 mb-0.5">Max DD</p>
                          <p className="text-sm font-medium text-loss tabular-nums">
                            {formatMetric(strategy.latest_metrics.max_drawdown, '-', '%')}
                          </p>
                        </div>
                      </div>
                    )}
                    <FiChevronRight className="w-5 h-5 text-gray-600 group-hover:text-gray-400 transition-colors" />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* CFTC Disclaimer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <CFTCDisclaimer />
      </div>

      {/* Create Strategy Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-gray-800 rounded-md w-full max-w-md p-6">
            <h2 className="text-lg font-medium text-white mb-4">New Strategy</h2>
            {createError && (
              <div className="mb-4 p-3 bg-red-900/30 border border-red-800/50 rounded-md">
                <p className="text-sm text-red-400">{createError}</p>
              </div>
            )}
            <div className="space-y-4">
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Name</label>
                <input
                  type="text"
                  value={newStrategyName}
                  onChange={(e) => {
                    setNewStrategyName(e.target.value);
                    setCreateError(null);
                  }}
                  placeholder="My Trading Strategy"
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500"
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm text-gray-400 mb-1.5">Description (optional)</label>
                <textarea
                  value={newStrategyDescription}
                  onChange={(e) => setNewStrategyDescription(e.target.value)}
                  placeholder="Describe your strategy..."
                  rows={3}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white placeholder-gray-500 focus:outline-none focus:border-indigo-500 resize-none"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setNewStrategyName('');
                  setNewStrategyDescription('');
                  setCreateError(null);
                }}
                className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateStrategy}
                disabled={!newStrategyName.trim() || creating}
                className="px-4 py-2 bg-white text-black font-medium rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
